import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { JsonLd } from '@/components/JsonLd';

export default function FAQ() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = t('faq.questions');

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Schema.org FAQPage structured data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{t('faq.title')} - A-More AI</title>
        <meta name="description" content="Trouvez les réponses à toutes vos questions sur A-More AI : fonctionnement, tarifs, sécurité, matching IA, protection des données. Support disponible 24/7." />
        <meta name="keywords" content="FAQ A-More AI, questions fréquentes, aide dating app, support rencontre IA, comment fonctionne matching" />
        <link rel="canonical" href={`${window.location.origin}/${language}/faq`} />
      </Helmet>

      <JsonLd data={faqSchema} />

      <div className="min-h-screen py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('faq.back')}
          </button>

          <div className="glass-card p-8">
            <div className="flex items-center gap-3 mb-6">
              <HelpCircle className="w-8 h-8 text-pink-500" />
              <h1 className="text-3xl font-bold gradient-text">{t('faq.title')}</h1>
            </div>

            <p className="text-gray-600 mb-8">
              Vous avez des questions ? Nous avons les réponses ! Consultez notre FAQ complète pour tout savoir sur A-More AI.
            </p>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-800 pr-4">{faq.q}</h3>
                    {openIndex === index ? (
                      <ChevronUp className="w-5 h-5 text-pink-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  
                  {openIndex === index && (
                    <div className="px-5 pb-5">
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-pink-50 to-orange-50 rounded-xl">
              <h3 className="font-bold text-gray-800 mb-2">Vous ne trouvez pas votre réponse ?</h3>
              <p className="text-gray-600 mb-4">
                Notre équipe support est là pour vous aider. Contactez-nous et nous vous répondrons dans les plus brefs délais.
              </p>
              <a
                href="mailto:support@a-more-ai.com"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-lg font-medium hover:shadow-lg transition-shadow"
              >
                Contacter le Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
